import { CanAccess } from '@modules/common/components/CanAccess';
import { KebabMenu } from '@modules/common/components/KebabMenu';
import { MenuItemOptions } from '@modules/common/components/KebabMenu/types';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { DisableIntegrationConfirmationModal } from '@modules/integrationsV2/components/DisableIntegrationConfirmationModal';
import { trackIntegrationPageEvent } from '@modules/integrationsV2/utils';
import { RemoveCircleOutlineRounded } from '@mui/icons-material';
import { Typography } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationType,
  PermissionOperation,
  PermissionResource,
} from 'generated/graphql';
import { UUID } from 'v2/core/entities/uuid';

interface IntegrationActionsMenuProps {
  integrationID: UUID;
  channelType: IntegrationChannelType;
  integrationType: IntegrationType;
  namespace: string | undefined;
}

export const IntegrationActionsMenu = ({
  channelType,
  integrationID,
  integrationType,
  namespace,
}: IntegrationActionsMenuProps) => {
  const { openModal } = useMuiModalContext();

  const openDisableIntegrationConfirmationModal = () => {
    openModal({
      contentComponent: () => (
        <DisableIntegrationConfirmationModal
          channelType={channelType}
          integrationID={integrationID}
        />
      ),
      modalProps: {
        PaperProps: {
          elevation: 3,
        },
        fullWidth: true,
        maxWidth: 'sm',
      },
    });
    trackIntegrationPageEvent({
      eventName: 'Integrations - Disable Integration Option Clicked',
      integration: {
        channelType,
        integrationType,
        namespace,
      },
    });
  };

  const kebabMenuItems: MenuItemOptions = [
    {
      icon: (
        <RemoveCircleOutlineRounded
          fontSize="small"
          sx={{
            color: 'error.light',
          }}
        />
      ),
      label: <Typography color="error.light">Disable Integration</Typography>,
      onClick: openDisableIntegrationConfirmationModal,
    },
  ];

  const onOpenMenu = () => {
    trackIntegrationPageEvent({
      eventName: 'Integrations - Integration Actions Menu Opened',
      integration: {
        channelType,
        integrationType,
        namespace,
      },
    });
  };

  return (
    <CanAccess
      resource={PermissionResource.Integration}
      operation={PermissionOperation.Write}
    >
      <KebabMenu
        onOpen={onOpenMenu}
        menuItems={kebabMenuItems}
        buttonProps={{
          color: 'primary',
          size: 'small',
        }}
        menuProps={{
          PaperProps: {
            sx: {
              minWidth: '150px',
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
        }}
      />
    </CanAccess>
  );
};
